import { apiBaseUrl } from './config';

const baseUrl = `${apiBaseUrl}/events`;

function buildQuery(params = {}) {
  const entries = Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== '');
  if (!entries.length) {
    return '';
  }

  const query = entries
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
    .join('&');

  return `?${query}`;
}

async function fetchJson(url, token, options = {}) {
  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(options.headers || {}),
    },
  });

  const data = await response.json();
  if (!response.ok) {
    throw new Error(data?.message || 'Error en solicitud de eventos.');
  }
  return data;
}

async function listEvents(token, params = {}) {
  const url = `${baseUrl}${buildQuery(params)}`;
  return await fetchJson(url, token);
}

async function createEvent(token, payload) {
  return await fetchJson(baseUrl, token, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

async function updateEvent(token, eventId, payload) {
  return await fetchJson(`${baseUrl}/${eventId}`, token, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
}

async function deleteEvent(token, eventId) {
  return await fetchJson(`${baseUrl}/${eventId}`, token, {
    method: 'DELETE',
  });
}

async function getEventById(token, eventId) {
  return await fetchJson(`${baseUrl}/${eventId}`, token);
}

async function listAvailableEvents(token, params = {}) {
  return await fetchJson(`${baseUrl}/available${buildQuery(params)}`, token);
}

async function getAvailableEventById(token, eventId) {
  return await fetchJson(`${baseUrl}/available/${eventId}`, token);
}

async function listMyRegistrations(token, params = {}) {
  return await fetchJson(`${baseUrl}/my-registrations${buildQuery(params)}`, token);
}

async function registerToEvent(token, eventId, payload = {}) {
  return await fetchJson(`${baseUrl}/${eventId}/register`, token, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

async function updateRegistrationAttendance(token, registrationId, attendanceRows) {
  return await fetchJson(`${baseUrl}/registrations/${registrationId}/attendance`, token, {
    method: 'PATCH',
    body: JSON.stringify({ attendanceRows }),
  });
}

async function deleteRegistration(token, registrationId) {
  return await fetchJson(`${baseUrl}/registrations/${registrationId}`, token, {
    method: 'DELETE',
  });
}

async function updateOfflinePayment(token, registrationId, payload) {
  return await fetchJson(`${baseUrl}/registrations/${registrationId}/offline-payment`, token, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
}

async function createEventCheckoutSession(token, registrationId) {
  return await fetchJson(`${baseUrl}/registrations/${registrationId}/checkout-session`, token, {
    method: 'POST',
  });
}

async function finalizeEventRegistrationPayment(token, registrationId) {
  return await fetchJson(`${baseUrl}/registrations/${registrationId}/finalize-payment`, token, {
    method: 'POST',
  });
}

function fetchEventCheckoutClientSecret(registrationId, token) {
  return async () => {
    const session = await createEventCheckoutSession(token, registrationId);
    return session.client_secret;
  };
}

async function downloadCertificate(token, registrationId) {
  const response = await fetch(`${baseUrl}/registrations/${registrationId}/certificate`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    let message = 'No se pudo descargar certificado.';
    try {
      const data = await response.json();
      if (data?.message) {
        message = data.message;
      }
    }
    catch (_e) {
      // Keep fallback message.
    }
    throw new Error(message);
  }

  const blob = await response.blob();
  const objectUrl = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = `certificado-evento-${registrationId}.pdf`;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  window.URL.revokeObjectURL(objectUrl);
}

async function downloadAttendeesCsv(token, params = {}) {
  const url = `${baseUrl}/reports/attendees.csv${buildQuery(params)}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    let message = 'No se pudo descargar el reporte CSV.';
    try {
      const data = await response.json();
      if (data?.message) {
        message = data.message;
      }
    }
    catch (_e) {
      // Keep fallback message.
    }
    throw new Error(message);
  }

  const content = await response.text();
  const fileName = `eventos-asistentes-${new Date().toISOString().slice(0, 10)}.csv`;
  const universalBOM = '\uFEFF';
  const anchor = document.createElement('a');
  anchor.href = 'data:text/csv; charset=utf-8,' + encodeURIComponent(universalBOM + content);
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
}

export {
  listEvents,
  createEvent,
  updateEvent,
  deleteEvent,
  downloadAttendeesCsv,
  getEventById,
  listAvailableEvents,
  getAvailableEventById,
  listMyRegistrations,
  registerToEvent,
  updateRegistrationAttendance,
  deleteRegistration,
  updateOfflinePayment,
  createEventCheckoutSession,
  finalizeEventRegistrationPayment,
  fetchEventCheckoutClientSecret,
  downloadCertificate,
};
